/**
 * @author 冷 (https://github.com/LengYXin)
 * @email lengyingxin8966@gmail.com
 * @create date 2021-09-13 15:33:18
 * @modify date 2021-09-13 15:33:18
 * @desc [微信认证]
 */
/// <reference path="wx.d.ts" />
import lodash from 'lodash';
import { BindAll, Debounce } from 'lodash-decorators';
import { action, computed, observable } from 'mobx';
import { Subject } from 'rxjs';
import { EnumAPI } from '../../apis';
import { EnumPlatform, EnumWechatAuthorizeState } from '../../enums';
import { AjaxBasics, Log } from '../../helpers';
import { BaseModel } from '../bases';
@BindAll()
export class WechatController {
  constructor(public appId) {}
  /**
   * WeixinJSBridge 异步数据加载订阅
   * @type {Promise<any>}
   * @memberof ControllerUser
   */
  readonly WeixinJSSubject = new Subject();
  readonly Model = new BaseModel({ type: 'object', storageKey: 'wechat' });
  /** 微信openID */
  @computed
  public get openid() {
    return lodash.get(this.Model.value, 'openid');
  }
  @computed
  public get weappOpenid() {
    return lodash.get(this.Model.value, 'openId');
  }
  @computed
  public get weappUnionId() {
    return lodash.get(this.Model.value, 'unionId');
  }
  /** 关注公众号 */
  @computed
  public get subscribe() {
    return lodash.eq(lodash.get(this.Model.value, 'subscribe'), 1);
  }
  /** 小程序环境 */
  @computed
  public get miniprogram() {
    return lodash.get(this.Model.value, 'miniprogram', false);
  }
  /** 当前属于大联想小程序子页面 */
  @computed
  public get miniprogramChildren() {
    return lodash.eq(
      lodash.get(this.Model.value, 'platform'),
      EnumPlatform.weapp,
    );
  }
  get WeixinJSBridge() {
    if (wx) {
      return wx;
    }
    return undefined;
  }
  /** 吊起公众号主页临时标识 */
  openOfficialAccountProfile = false;
  /** 初始化 */
  @observable
  isReady = false;
  /** 加载 */
  loading = false;
  jsApiList: Array<any> = [
    'updateAppMessageShareData',
    'updateTimelineShareData',
    // 'onMenuShareWeibo',
    // 'onMenuShareQZone',
    // 'startRecord',
    // 'stopRecord',
    // 'onVoiceRecordEnd',
    // 'playVoice',
    // 'pauseVoice',
    // 'stopVoice',
    // 'onVoicePlayEnd',
    // 'uploadVoice',
    // 'downloadVoice',
    'chooseImage',
    // 'previewImage',
    'uploadImage',
    // 'downloadImage',
    // 'translateVoice',
    // 'getNetworkType',
    // 'openLocation',
    // 'getLocation',
    // 'hideOptionMenu',
    // 'showOptionMenu',
    // 'hideMenuItems',
    'showMenuItems',
    // 'hideAllNonBaseMenuItem',
    // 'showAllNonBaseMenuItem',
    // 'closeWindow',
    'scanQRCode',
    'chooseWXPay',
    // 'openProductSpecificView',
    // 'addCard',
    // 'chooseCard',
    // 'openCard'
  ];
  openTagList: Array<any> = ['wx-open-launch-weapp', 'wx-open-subscribe'];
  @Debounce(999)
  @action
  toReady(isReady: boolean) {
    this.isReady = isReady;
  }
  onComplete() {
    this.WeixinJSSubject.next(true);
    this.WeixinJSSubject.complete();
  }
  async onReady(signUrl: string) {
    if (this.loading) {
      return Log.warning(` Weixin 加载中`);
    }
    if (this.isReady) {
      return Log.warning(` Weixin 已经加载`);
    }
    this.loading = true;
    const config = await AjaxBasics.request<{
      debug?: boolean; // 开启调试模式,调用的所有api的返回值会在客户端alert出来，若要查看传入的参数，可以在pc端打开，参数信息会通过log打出，仅在pc端时才会打印。
      appId: string; // 必填，公众号的唯一标识
      timestamp: number; // 必填，生成签名的时间戳
      nonceStr: string; // 必填，生成签名的随机串
      signature: string; // 必填，签名，见附录1
      jsApiList: Array<any>; // 必填，需要使用的JS接口列表，所有JS接口列表见附录2
      openTagList?: Array<any>;
    }>({
      ...EnumAPI.wechat_jsapi_sign,
      body: { signUrl },
      headers: { 'x-app-id': this.appId },
    });
    try {
      this.WeixinJSBridge.miniProgram.getEnv((res) => {
        console.log('LENG ~ WechatController ~ miniProgram ~ getEnv', res);
        this.Model.merge(res);
      });
      const res = await new Promise((res, rej) => {
        Log.info(` Weixin onReady`, config);
        this.WeixinJSBridge.ready(() => {
          console.log(
            'LENG ~ WechatController ~ this.WeixinJSBridge.ready ~ ready',
          );
          res(config);
        });
        this.WeixinJSBridge.error((error) => {
          console.log(
            'LENG ~ WechatController ~ this.WeixinJSBridge.error ~ error',
            error,
          );
          rej(error);
        });
        this.WeixinJSBridge.config({
          jsApiList: this.jsApiList,
          openTagList: this.openTagList,
          appId: config.appId,
          nonceStr: config.nonceStr,
          signature: config.signature,
          timestamp: config.timestamp,
        });
      });
      this.toReady(true);
      this.loading = false;
      console.log('LENG ~ WechatController ~ miniProgram ~ getEnv');
      this.WeixinJSSubject.next(true);
      this.WeixinJSSubject.complete();
      return res;
    } catch (error) {
      this.loading = false;
      this.WeixinJSSubject.error(error);
      this.WeixinJSSubject.complete();
      throw error;
    }
  }
  /**
   * 获取“分享给朋友”及“分享到QQ”按钮点击状态及自定义分享内容接口（新）
   */
  updateAppMessageShareData(params: wx.IupdateAppMessageShareData) {
    Log.info('【 updateAppMessageShareData 】', params);
    this.WeixinJSSubject.toPromise().then(() =>
      this.WeixinJSBridge.updateAppMessageShareData(params),
    );
    return params;
  }
  /**
   * 获取“分享到朋友圈”及“分享到QQ空间”按钮点击状态及自定义分享内容接口（新）
   */
  updateTimelineShareData(params: wx.IupdateTimelineShareData) {
    Log.info('【 updateTimelineShareData 】', params);
    this.WeixinJSSubject.toPromise().then(() =>
      this.WeixinJSBridge.updateTimelineShareData(params),
    );
    return params;
  }
  /**
   * 调起微信扫一扫接口
   */
  scanQRCode(params: wx.IscanQRCode) {
    Log.info('【 scanQRCode 】', params);
    this.WeixinJSSubject.toPromise().then(() =>
      this.WeixinJSBridge.scanQRCode(params),
    );
    return params;
  }
  /**
   * 获取微信小程序 appid
   * @param jsCode
   */
  async onWeappSession(jsCode, appId) {
    const res = await AjaxBasics.request({
      ...EnumAPI.wechat_weapp_session,
      body: { jsCode },
      headers: { 'x-app-id': appId },
    });
    this.Model.merge(res);
    return res;
  }
  async onAuth(authCode) {
    const res = await AjaxBasics.request({
      ...EnumAPI.wechat_login,
      body: { authCode },
      headers: { 'x-app-id': this.appId },
    });
    const info = await AjaxBasics.request({
      ...EnumAPI.wechat_info,
      body: { openId: lodash.get(res, 'openid') },
      headers: { 'x-app-id': this.appId },
    });
    this.Model.merge(lodash.assign(res, info));
    return res;
  }
  async onSend() {
    return AjaxBasics.request({
      ...EnumAPI.wechat_send,
      body: {
        templateId: 'eYV50ZNOMZIajfYW7lSsdPOrL9XXMxOpkJ_FGOJ-u2Y',
        openId: this.openid,
        params: {
          thing11: '商机状态通知',
          phrase2: '测试状态',
        },
      },
      headers: { 'x-app-id': this.appId },
    });
  }
  /**
   * 授权 url https://developers.weixin.qq.com/doc/offiaccount/OA_Web_Apps/Wechat_webpage_authorization.html#0
   * @param redirect_uri
   */
  getAuthorizeUrl(
    redirect_uri,
    scope: 'snsapi_base' | 'snsapi_userinfo' = 'snsapi_userinfo',
    state: EnumWechatAuthorizeState = EnumWechatAuthorizeState.login,
  ) {
    const url = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${
      this.appId
    }&redirect_uri=${encodeURIComponent(
      redirect_uri,
    )}&response_type=code&scope=${scope}&state=${state}#wechat_redirect`;
    Log.success('AuthorizeUrl', url);
    return url;
  }

  /**
   * @description: 获取绑定url
   * @param {*}
   * @return {*}
   */
  async onSubscriptionBusBoLink(body) {
    return await AjaxBasics.request<any>(
      lodash.assign({
        ...EnumAPI.priceReview_subscriptionT1BusBoLink,
        body,
      }),
    );
  }

  /**
   * @description: 获取绑定用户信息
   * @param {*}
   * @return {*}
   */
  async onBusinessBindUser() {
    return await AjaxBasics.request<any>(
      lodash.assign(EnumAPI.priceReview_bindUserInfo),
    );
  }

  /**
   * @description: 设置微信绑定用户消息提醒开关
   * @param {*}
   * @return {*}
   */
  async onMessageControl(entity) {
    return await AjaxBasics.request<any>(
      lodash.assign(EnumAPI.priceReview_messageControl, { body: entity }),
    );
  }

  /**
   * @description: 获取微信绑定用户消息提醒设置
   * @param {*}
   * @return {*}
   */
  async onMessageInfo() {
    return await AjaxBasics.request<any>(
      lodash.assign(EnumAPI.priceReview_messageInfo),
    );
  }
}
